/* modules imports */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

/* types imports */
import type { SnackbarRefProps } from "components/Snackbar";
import type { AnimationEvent, FC } from "react";

/* project files imports */
import IconAbout from "assets/images/icon-about.svg";
import logo from "assets/images/logo-big.png";
import { ButtonSize, ButtonVariant } from "components/Button";
import ModalAbout from "components/Modal/About";
import Snackbar, { SnackbarType } from "components/Snackbar";
import { Status as AuthStatus } from "constants/auth";
import { DemoStep } from "./constants";
import Demo from "./Demo";

/* styles imports */
import {
  AboutIcon,
  AboutLink,
  BoldText,
  ButtonWrapper,
  Content,
  Image,
  StyledButton,
  Text,
  Wrapper
} from "./index.styles";

type LoginPageProps = {
  authStatus: AuthStatus;
};

const LoginPage: FC<LoginPageProps> = ({ authStatus }) => {
  const ref = useRef<SnackbarRefProps>(null);
  const navigate = useNavigate();
  const [isImageLoaded, setIsImageLoaded] = useState<boolean>(false);
  // skip the demo for people coming back from a sign-in error
  const [demoStep, setDemoStep] = useState<DemoStep>(() => {
    const hasError = new URL(window.location?.toString()).searchParams.has("error");

    return hasError || authStatus === AuthStatus.ERRORED
      ? DemoStep.DONE
      : DemoStep.PLAYING;
  });
  const [isModalAboutOpen, setIsModalAboutOpen] = useState<boolean>(false);

  useEffect(() => {
    const location = new URL(window.location?.toString());
    const err = location?.searchParams?.get("error");

    if (err) {
      navigate("/");

      if (err === "InternalServerError") {
        ref.current?.open("The server encountered an unexpected error");
      }
    } else if (authStatus === AuthStatus.ERRORED) {
      ref.current?.open("An error has occurred.\nYou may need to log in again.");
    }
  }, []);

  const services = [
    { name: "Spotify", path: "/auth/spotify" },
    { name: "Deezer", path: "/auth/deezer" },
    { name: "Last.fm", path: "/auth/lastfm" }
  ];

  const handleWrapperAnimationEnd = (event: AnimationEvent<HTMLDivElement>): void => {
    // ignore the animations of the children
    if (event.target === event.currentTarget && demoStep === DemoStep.LEAVING) {
      setDemoStep(DemoStep.DONE);
    }
  };

  return (
    <Content>
      <Wrapper demoStep={demoStep} onAnimationEnd={handleWrapperAnimationEnd}>
        <Image
          alt="fmk big logo"
          onLoad={() => setIsImageLoaded(true)}
          src={logo}
        />
        {isImageLoaded && demoStep !== DemoStep.DONE && (
          <Demo onDone={() => setDemoStep(DemoStep.LEAVING)} />
        )}
        {isImageLoaded && demoStep === DemoStep.DONE && <ButtonWrapper>
          <Text>Now play with your own top artists!</Text>
          {services.map((service, index) => (
            <StyledButton
              key={index}
              index={index}
              onClick={
                () => {
                  if (service.path) {
                    window.location.assign(
                      process.env.REACT_APP__API__BASE_URL + service.path
                    );
                  }
                }
              }
              size={ButtonSize.LARGE}
              variant={ButtonVariant.OUTLINED}
            >
              Authenticate using <BoldText>{service.name}</BoldText>
            </StyledButton>
          ))}
          <AboutLink onClick={() => setIsModalAboutOpen(true)}>
            <AboutIcon alt="about icon" src={IconAbout} />
            About
          </AboutLink>
        </ButtonWrapper>}
      </Wrapper>

      <ModalAbout
        hasEmails={false}
        isOpen={isModalAboutOpen}
        onClose={() => setIsModalAboutOpen(false)}
      />
      <Snackbar ref={ref} type={SnackbarType.ERROR} />
    </Content>
  );
};

export default LoginPage;
